import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getSummary, IGrowthSummary } from "api/student/v2/growth.api";
import { formatTimeAgo } from "helpers/Datetime.helper";

// 스켈레톤 컴포넌트
const SkeletonMainCard = () => (
    <div className="flex justify-center mb-6 animate-pulse">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 w-full max-w-xs">
            <div className="text-center">
                <div className="flex items-center justify-center mx-auto mb-3">
                    <div className="w-6 h-6 bg-gray-200 rounded"></div>
                </div>
                <div className="bg-gray-200 rounded h-4 w-24 mx-auto mb-2"></div>
                <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto"></div>
            </div>
        </div>
    </div>
);

const SkeletonStatsCard = () => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-pulse">
        <div className="text-center">
            <div className="flex items-center justify-center mx-auto mb-2">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="bg-gray-200 rounded h-3 w-16 mx-auto mb-1"></div>
            <div className="w-12 h-12 bg-gray-200 rounded-full mx-auto"></div>
        </div>
    </div>
);

const SkeletonDiaryCard = () => (
    <div className="bg-white rounded-lg shadow-sm p-4 animate-pulse">
        <div className="bg-gray-200 rounded h-3 w-24 mb-3"></div>
        <div className="space-y-2">
            <div className="bg-gray-200 rounded h-4 w-full"></div>
            <div className="bg-gray-200 rounded h-4 w-3/4"></div>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="bg-gray-200 rounded h-4 w-8"></div>
                    <div className="bg-gray-200 rounded h-4 w-8"></div>
                    <div className="bg-gray-200 rounded h-4 w-8"></div>
                    <div className="bg-gray-200 rounded h-4 w-8"></div>
                </div>
                <div className="bg-gray-200 rounded h-3 w-12"></div>
            </div>
        </div>
    </div>
);

const GrowthSummary = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [summaryData, setSummaryData] = useState<IGrowthSummary | null>(null);
    const [error, setError] = useState<string | null>(null);

    // API 데이터 로드
    const fetchSummaryData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await getSummary();
            console.log("response", { response });
            setSummaryData(response.summary);
        } catch (err) {
            console.error("성장 요약 데이터 로드 실패:", err);
            setError(getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLoadError"));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSummaryData();
    }, []);

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 py-4">
                {/* 헤더는 항상 표시 */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtTitle")}
                    </h1>
                    <p className="text-gray-600 text-sm">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtDesc")}
                    </p>
                </div>

                {isLoading ? (
                    <>
                        {/* 총 다이어리 수 스켈레톤 */}
                        <SkeletonMainCard />
                        
                        {/* 기타 통계 카드 스켈레톤 */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                            <SkeletonStatsCard />
                            <SkeletonStatsCard />
                            <SkeletonStatsCard />
                            <SkeletonStatsCard />
                        </div>
                        
                        {/* 인기 다이어리 스켈레톤 */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <SkeletonDiaryCard />
                            <SkeletonDiaryCard />
                            <SkeletonDiaryCard />
                            <SkeletonDiaryCard />
                        </div>
                    </>
                ) : error ? (
                    <div className="text-center py-12">
                        <div className="text-red-500 text-lg mb-4">{error}</div>
                        <button
                            onClick={fetchSummaryData}
                            className="px-6 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#B8326B] transition-colors"
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtRetry")}
                        </button>
                    </div>
                ) : !summaryData ? (
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNoData")}</div>
                    </div>
                ) : (
                    <>

                {/* 총 다이어리 수 - 별도 섹션 */}
                <div className="flex justify-center mb-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 w-full max-w-xs">
                        <div className="text-center">
                            <div className="flex items-center justify-center mx-auto mb-3">
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-2">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtTotalDiary")}</h3>
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.totalDiaryCount}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 기타 통계 카드 */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">

                    {/* 총 좋아요 수 - 2순위 */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="flex items-center justify-center mx-auto mb-2">
                                <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtTotalLikes")}</h3>
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                <p className="text-xl font-bold text-gray-900">{summaryData.totalLikeCount}</p>
                            </div>
                        </div>
                    </div>

                    {/* 총 댓글 수 - 3순위 */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="flex items-center justify-center mx-auto mb-2">
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtTotalComments")}</h3>
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                <p className="text-xl font-bold text-gray-900">{summaryData.totalCommentCount}</p>
                            </div>
                        </div>
                    </div>

                    {/* 캐스팅 매니저 조회수 - 4순위 */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="flex items-center justify-center mx-auto mb-2">
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                            </div>
                            <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtCastingManagerViews")}</h3>
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                <p className="text-xl font-bold text-gray-900">{summaryData.totalCastingManagerViewCount}</p>
                            </div>
                        </div>
                    </div>

                    {/* 총 북마크 수 - 5순위 */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <div className="text-center">
                            <div className="flex items-center justify-center mx-auto mb-2">
                                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                </svg>
                            </div>
                            <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtTotalBookmarks")}</h3>
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                <p className="text-xl font-bold text-gray-900">{summaryData.totalBookmarkCount}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 활동 통계 - 중간 우선순위 */}
                {/* <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtActivityStats")}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.recentDiaryCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtRecent7Days")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.weeklyDiaryCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLastWeek")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.thisMonthDiaryCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtThisMonth")}</p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.weeklyLikeCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLastWeekLikes")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.weeklyCommentCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLastWeekComments")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.thisWeekDiaryCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtThisWeek")}</p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.weeklyCastingManagerViewCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLastWeekCastingViews")}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-1">
                                <p className="text-2xl font-bold text-gray-900">{summaryData.monthlyCastingManagerViewCount}</p>
                            </div>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtThisMonthCastingViews")}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm font-bold text-gray-900">
                                {summaryData.lastDiaryCreatedAt ? formatTimeAgo(summaryData.lastDiaryCreatedAt) : getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNone")}
                            </p>
                            <p className="text-[10px] text-gray-600">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtLastDiaryDate")}</p>
                        </div>
                    </div>
                </div> */}

                {/* 인기 다이어리 - 낮은 우선순위 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* 가장 좋아요가 많은 다이어리 - 1순위 */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <h3 className="text-[10px] font-medium text-gray-600 mb-3">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtMostLikedDiary")}</h3>
                        {summaryData.mostLikedDiary ? (
                            <Link
                                to={`/student/growth/diary/${summaryData.mostLikedDiary.id}`}
                                className="block hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                            >
                                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                                    {summaryData.mostLikedDiary.title}
                                </h4>
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-[#CF3688]" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                            {summaryData.mostLikedDiary.likeCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            {summaryData.mostLikedDiary.commentCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            {summaryData.mostLikedDiary.viewCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            {summaryData.mostLikedDiary.bookmarkCount}
                                        </span>
                                    </div>
                                    <span className="text-xs">{formatTimeAgo(summaryData.mostLikedDiary.createdAt)}</span>
                                </div>
                            </Link>
                        ) : (
                            <p className="text-sm text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNoPopularDiary")}</p>
                        )}
                    </div>

                    {/* 가장 댓글이 많은 다이어리 - 2순위 */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <h3 className="text-[10px] font-medium text-gray-600 mb-3">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtMostCommentedDiary")}</h3>
                        {summaryData.mostCommentedDiary ? (
                            <Link
                                to={`/student/growth/diary/${summaryData.mostCommentedDiary.id}`}
                                className="block hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                            >
                                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                                    {summaryData.mostCommentedDiary.title}
                                </h4>
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                            {summaryData.mostCommentedDiary.likeCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-[#CF3688]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            {summaryData.mostCommentedDiary.commentCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            {summaryData.mostCommentedDiary.viewCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            {summaryData.mostCommentedDiary.bookmarkCount}
                                        </span>
                                    </div>
                                    <span className="text-xs">{formatTimeAgo(summaryData.mostCommentedDiary.createdAt)}</span>
                                </div>
                            </Link>
                        ) : (
                            <p className="text-sm text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNoCommentedDiary")}</p>
                        )}
                    </div>

                    {/* 캐스팅 매니저가 가장 많이 본 다이어리 - 3순위 (새로 추가) */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <h3 className="text-[10px] font-medium text-gray-600 mb-3">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtMostViewedByCastingManager")}</h3>
                        {summaryData.mostViewedDiary ? (
                            <Link
                                to={`/student/growth/diary/${summaryData.mostViewedDiary.id}`}
                                className="block hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                            >
                                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                                    {summaryData.mostViewedDiary.title}
                                </h4>
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                            {summaryData.mostViewedDiary.likeCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            {summaryData.mostViewedDiary.commentCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-[#CF3688]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            {summaryData.mostViewedDiary.viewCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            {summaryData.mostViewedDiary.bookmarkCount}
                                        </span>
                                    </div>
                                    <span className="text-xs">{formatTimeAgo(summaryData.mostViewedDiary.createdAt)}</span>
                                </div>
                            </Link>
                        ) : (
                            <p className="text-sm text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNoCastingManagerDiary")}</p>
                        )}
                    </div>

                    {/* 가장 북마크가 많은 다이어리 - 4순위 (새로 추가) */}
                    <div className="bg-white rounded-lg shadow-sm p-4">
                        <h3 className="text-[10px] font-medium text-gray-600 mb-3">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtMostBookmarkedDiary")}</h3>
                        {summaryData.mostBookmarkedDiary ? (
                            <Link
                                to={`/student/growth/diary/${summaryData.mostBookmarkedDiary.id}`}
                                className="block hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                            >
                                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                                    {summaryData.mostBookmarkedDiary.title}
                                </h4>
                                <div className="flex items-center justify-between text-sm text-gray-600">
                                    <div className="flex items-center gap-4">
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                            </svg>
                                            {summaryData.mostBookmarkedDiary.likeCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                            {summaryData.mostBookmarkedDiary.commentCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            {summaryData.mostBookmarkedDiary.viewCount}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <svg className="w-4 h-4 text-[#CF3688]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                            </svg>
                                            {summaryData.mostBookmarkedDiary.bookmarkCount}
                                        </span>
                                    </div>
                                    <span className="text-xs">{formatTimeAgo(summaryData.mostBookmarkedDiary.createdAt)}</span>
                                </div>
                            </Link>
                        ) : (
                            <p className="text-sm text-gray-500">{getLanguageValue(PAGE_TYPE.STUDENT, "growthSummary.txtNoBookmarkedDiary")}</p>
                        )}
                    </div>
                </div>
                    </>
                )}
            </div>
        </main>
    );
};
export default GrowthSummary;
